/**
 * Phase J-UX — Inline Edit dialog for an existing journal entry.
 *
 * Author-only — surfaced from the entry card's ••• menu. PATCHes
 * /api/journal/{id} with the changed fields. The dialog mirrors
 * the New Entry dialog where it makes sense (title, body, mood,
 * mood tags, location) but excludes attachments because
 * re-uploading attachments would need its own flow.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MapPin } from "lucide-react";
import { MoodPicker } from "@/components/journal/MoodPicker";
import { api } from "@/lib/api";

export function JournalEditDialog({ open, onOpenChange, entry, onSaved }) {
  const [form, setForm] = useState({
    title: "",
    body: "",
    mood: null,
    mood_tags: [],
    location: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (entry) {
      setForm({
        title: entry.title || "",
        body: entry.body || "",
        mood: entry.mood ?? null,
        mood_tags: entry.mood_tags || [],
        location: entry.location || "",
      });
    }
  }, [entry]);

  if (!entry) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Title and details are both required.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.patch(`/journal/${entry.id}`, form);
      toast.success("Entry updated");
      onOpenChange(false);
      onSaved && onSaved(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update entry.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white border-[#D9E1E3] max-h-[90vh] overflow-y-auto"
        data-testid="journal-edit-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">Edit journal entry</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 min-w-0">
          <div>
            <Label>Title</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
              data-testid="journal-edit-title"
            />
          </div>
          <div>
            <Label>Details</Label>
            <Textarea
              rows={6}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              required
              data-testid="journal-edit-body"
            />
          </div>
          <MoodPicker
            value={form.mood}
            onChange={(v) => setForm({ ...form, mood: v })}
            tags={form.mood_tags}
            onTagsChange={(t) => setForm({ ...form, mood_tags: t })}
          />
          <div>
            <Label className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" strokeWidth={1.7} /> Location (optional)
            </Label>
            <Input
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="e.g. School pickup, Contact centre, Home"
              maxLength={200}
              data-testid="journal-edit-location"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="journal-edit-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              className="bg-[#1F9FA4] hover:bg-[#178085]"
              data-testid="journal-edit-save"
            >
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default JournalEditDialog;
